// ═══════════════════════════════════════════════════════════════════════════
// PanneauModeles.jsx
// Panneau Modèles de données — PesacoreIH
// Connexion exclusive à usePesacoreIH
// ═══════════════════════════════════════════════════════════════════════════

import {
  AlertTriangle,
  ChevronRight,
  Database,
  GitBranch,
  Info,
  Layers,
  Link,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
  Zap
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import api from '../../config/axiosConfig';
import usePesacoreIH from '../../stores/usePesacore';

// ─── Hooks locaux ─────────────────────────────────────────────────────────

function useModelsData() {
  const { prepareAction, executeAction, dryRunMode } = usePesacoreIH();

  const [collections, setCollections]   = useState([]);
  const [dbStats, setDbStats]           = useState(null);
  const [relations, setRelations]       = useState({ nodes: [], edges: [] });
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [colRes, statsRes, relRes] = await Promise.all([
        api.get('/superadmin/models'),
        api.get('/superadmin/models/db-stats'),
        api.get('/superadmin/models/relations'),
      ]);
      setCollections(colRes.data.collections || []);
      setDbStats(statsRes.data);
      setRelations(relRes.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCollectionDetail = async (name) => {
    try {
      const res = await api.get(`/superadmin/models/${name}?sampleSize=3`);
      return res.data;
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message);
    }
  };

  const createIndex = async (collectionName, fields, options) => {
    const prep = prepareAction('CREATE_INDEX', { collectionName, fields, options });
    if (!prep.valid) return { success: false, errors: prep.errors };
    if (prep.dryRun) return { success: true, dryRun: true };

    return executeAction(prep.commandId, async () => {
      const res = await api.post('/superadmin/models/index', { collectionName, fields, options });
      await fetchAll();
      return res.data;
    });
  };

  const dropIndex = async (collectionName, indexName) => {
    const prep = prepareAction('DROP_INDEX', { collectionName, indexName });
    if (!prep.valid) return { success: false, errors: prep.errors };
    if (prep.dryRun) return { success: true, dryRun: true };

    return executeAction(prep.commandId, async () => {
      const res = await api.delete(`/superadmin/models/${collectionName}/index/${indexName}`);
      await fetchAll();
      return res.data;
    });
  };

  const dropCollection = async (collectionName, confirmationToken) => {
    const prep = prepareAction('DROP_COLLECTION', { collectionName });
    if (!prep.valid) return { success: false, errors: prep.errors };
    if (prep.dryRun) return { success: true, dryRun: true };

    return executeAction(prep.commandId, async () => {
      const res = await api.delete(`/superadmin/models/${collectionName}`, {
        data: { confirmationToken },
      });
      await fetchAll();
      return res.data;
    });
  };

  useEffect(() => { fetchAll(); }, []);

  return {
    collections, dbStats, relations,
    loading, error,
    fetchAll, fetchCollectionDetail,
    createIndex, dropIndex, dropCollection,
    dryRunMode,
  };
}

// ─── Composants UI ────────────────────────────────────────────────────────

function Badge({ children, color = 'zinc' }) {
  const colors = {
    zinc:    'bg-zinc-800 text-zinc-400 border-zinc-700',
    sky:     'bg-sky-950/40 text-sky-300 border-sky-800/40',
    emerald: 'bg-emerald-950/40 text-emerald-300 border-emerald-800/40',
    amber:   'bg-amber-950/40 text-amber-300 border-amber-800/40',
    rose:    'bg-rose-950/40 text-rose-300 border-rose-800/40',
    purple:  'bg-purple-950/40 text-purple-300 border-purple-800/40',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono border ${colors[color]}`}>
      {children}
    </span>
  );
}

function TypeBadge({ type }) {
  const map = {
    String:   'sky',   ObjectId: 'purple', Number: 'emerald',
    Boolean:  'amber', Date:     'rose',   Array:  'zinc',
    Mixed:    'zinc',  Object:   'zinc',   Buffer: 'zinc',
  };
  return <Badge color={map[type] || 'zinc'}>{type}</Badge>;
}

// ─── Carte collection (liste) ─────────────────────────────────────────────

function CollectionCard({ col, isSelected, onClick }) {
  const sizeColor = col.storageSizeKB > 100_000
    ? 'text-red-400' : col.storageSizeKB > 10_000
    ? 'text-amber-400' : 'text-zinc-500';

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 rounded-xl border transition-all ${
        isSelected
          ? 'bg-orange-950/30 border-orange-700/50'
          : 'bg-zinc-900/40 border-zinc-800/60 hover:border-zinc-700'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Database size={14} className={isSelected ? 'text-orange-400' : 'text-zinc-600'} />
          <span className={`text-sm font-mono font-bold truncate ${isSelected ? 'text-orange-300' : 'text-zinc-200'}`}>
            {col.name}
          </span>
          {col.hasMongooseModel && (
            <Badge color="emerald">mongoose</Badge>
          )}
        </div>
        <ChevronRight size={14} className={`shrink-0 mt-0.5 transition-transform ${isSelected ? 'rotate-90 text-orange-400' : 'text-zinc-700'}`} />
      </div>

      <div className="mt-2 flex items-center gap-4 text-[10px]">
        <span className="text-zinc-500">{col.count.toLocaleString()} docs</span>
        <span className={sizeColor}>{(col.storageSizeKB / 1024).toFixed(1)} MB</span>
        <span className="text-zinc-700">{col.nindexes} index</span>
        {col.mongooseModel && (
          <span className="text-emerald-600 truncate">{col.mongooseModel}</span>
        )}
      </div>
    </button>
  );
}

// ─── Détail collection ────────────────────────────────────────────────────

function CollectionDetail({ col, onCreateIndex, onDropIndex, onDropCollection, dryRunMode }) {
  const [detail, setDetail]         = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab]   = useState('fields');
  const [showDropForm, setShowDropForm]   = useState(false);
  const [showIndexForm, setShowIndexForm] = useState(false);
  const [feedback, setFeedback]     = useState(null);
  const { fetchCollectionDetail }   = useModelsData();

  useEffect(() => {
    setDetail(null);
    setActiveTab('fields');
    setFeedback(null);
    setDetailLoading(true);

    // Récupération locale sans re-fetch global
    api.get(`/superadmin/models/${col.name}?sampleSize=3`)
      .then(r => setDetail(r.data))
      .catch(err => setFeedback({ type: 'error', msg: err.response?.data?.error || err.message }))
      .finally(() => setDetailLoading(false));
  }, [col.name]);

  const showFeedback = (type, msg) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 4000);
  };

  const tabs = [
    { id: 'fields',  label: 'Champs',   icon: Layers  },
    { id: 'indexes', label: 'Index',    icon: Zap     },
    { id: 'sample',  label: 'Échantillon', icon: Info },
  ];

  const fields = detail?.fields || col.fields;

  return (
    <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl overflow-hidden flex flex-col h-full">

      {/* Header */}
      <div className="px-5 py-4 border-b border-zinc-800/60 bg-black/30">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-black text-zinc-100 font-mono">{col.name}</h3>
            <div className="flex items-center gap-3 mt-1 text-[10px] text-zinc-600">
              <span>{(detail?.count ?? col.count).toLocaleString()} documents</span>
              <span>{((detail?.storageSizeKB ?? col.storageSizeKB) / 1024).toFixed(2)} MB</span>
              {col.mongooseModel && <span className="text-emerald-500">Model: {col.mongooseModel}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowIndexForm(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded text-zinc-300 text-xs"
            >
              <Plus size={12} />
              Index
            </button>
            <button
              onClick={() => setShowDropForm(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-950/30 hover:bg-red-950/50 border border-red-800/30 rounded text-red-400 text-xs"
            >
              <Trash2 size={12} />
              Drop
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs transition-all ${
                activeTab === tab.id
                  ? 'bg-orange-950/40 border border-orange-700/50 text-orange-300'
                  : 'text-zinc-600 hover:text-zinc-400'
              }`}
            >
              <tab.icon size={12} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Feedback */}
      {feedback && (
        <div className={`mx-4 mt-3 px-4 py-2.5 rounded text-xs border ${
          feedback.type === 'error'   ? 'bg-red-950/30 border-red-800/30 text-red-300' :
          feedback.type === 'dry-run' ? 'bg-amber-950/30 border-amber-800/30 text-amber-300' :
                                        'bg-emerald-950/30 border-emerald-800/30 text-emerald-300'
        }`}>
          {feedback.type === 'dry-run' && '🧪 DRY-RUN — '}{feedback.msg}
        </div>
      )}

      {/* Contenu */}
      <div className="flex-1 overflow-auto p-4">

        {detailLoading && (
          <div className="text-center py-8 text-zinc-700 text-xs">Chargement...</div>
        )}

        {/* Tab : Champs */}
        {!detailLoading && activeTab === 'fields' && (
          <div className="space-y-1">
            {fields ? (
              Object.entries(fields).map(([fieldName, info]) => (
                <div key={fieldName} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-zinc-800/30 group">
                  <span className="font-mono text-xs text-zinc-300 w-40 truncate">{fieldName}</span>
                  <TypeBadge type={info.type} />
                  <div className="flex items-center gap-2 ml-2 flex-wrap">
                    {info.required && <Badge color="rose">required</Badge>}
                    {info.unique   && <Badge color="amber">unique</Badge>}
                    {info.ref      && (
                      <span className="flex items-center gap-1 text-[10px] text-purple-400">
                        <Link size={10} />
                        {info.ref}
                      </span>
                    )}
                    {info.enum?.length > 0 && (
                      <span className="text-[10px] text-zinc-600 truncate max-w-[200px]">
                        enum: {info.enum.join(', ')}
                      </span>
                    )}
                    {info.example && (
                      <span className="text-[10px] text-zinc-700 italic truncate max-w-[150px]">
                        ex: {info.example}
                      </span>
                    )}
                  </div>
                  {info.default !== undefined && (
                    <span className="ml-auto text-[10px] text-zinc-700 font-mono">
                      default: {info.default}
                    </span>
                  )}
                </div>
              ))
            ) : (
              <p className="text-zinc-700 text-xs py-4 text-center">
                Aucun schéma Mongoose — chargez un échantillon pour inférer les champs
              </p>
            )}
          </div>
        )}

        {/* Tab : Index */}
        {!detailLoading && activeTab === 'indexes' && (
          <div className="space-y-2">
            {(detail?.indexes || col.indexes || []).map(idx => (
              <div key={idx.name} className="flex items-center justify-between p-3 bg-zinc-900/50 border border-zinc-800/40 rounded-lg">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-zinc-200">{idx.name}</span>
                    {idx.unique && <Badge color="amber">unique</Badge>}
                    {idx.sparse && <Badge color="sky">sparse</Badge>}
                    {idx.ttl   && <Badge color="rose">TTL {idx.ttl}s</Badge>}
                  </div>
                  <div className="text-[10px] text-zinc-600 mt-1 font-mono">
                    {JSON.stringify(idx.key)}
                  </div>
                </div>
                {idx.name !== '_id_' && (
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Supprimer l'index "${idx.name}" ?`)) return;
                      const r = await onDropIndex(col.name, idx.name);
                      showFeedback(
                        r.dryRun ? 'dry-run' : r.success ? 'success' : 'error',
                        r.dryRun ? `DRY-RUN : suppression index ${idx.name}` : r.success ? `Index supprimé` : (r.errors?.join(' ') || r.error)
                      );
                    }}
                    className="p-1.5 text-zinc-700 hover:text-red-400 transition-colors"
                    title="Supprimer cet index"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            ))}
            {(detail?.indexes || col.indexes || []).length === 0 && (
              <p className="text-zinc-700 text-xs py-4 text-center">Aucun index</p>
            )}
          </div>
        )}

        {/* Tab : Échantillon */}
        {!detailLoading && activeTab === 'sample' && (
          <div className="space-y-3">
            {(detail?.sample || []).length === 0 ? (
              <p className="text-zinc-700 text-xs py-4 text-center">Collection vide ou données non disponibles</p>
            ) : (
              detail.sample.map((doc, i) => (
                <div key={i} className="bg-black/40 border border-zinc-800/40 rounded-lg p-3">
                  <div className="text-[10px] text-zinc-600 mb-2">Document #{i + 1}</div>
                  <pre className="text-[10px] text-zinc-400 font-mono overflow-auto max-h-40 whitespace-pre-wrap">
                    {JSON.stringify(doc, null, 2)}
                  </pre>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Modal : Créer index */}
      {showIndexForm && (
        <CreateIndexModal
          collectionName={col.name}
          onClose={() => setShowIndexForm(false)}
          onSubmit={async (fields, options) => {
            const r = await onCreateIndex(col.name, fields, options);
            showFeedback(
              r.dryRun ? 'dry-run' : r.success ? 'success' : 'error',
              r.dryRun ? 'DRY-RUN : index simulé, non créé' : r.success ? 'Index créé' : (r.errors?.join(' ') || r.error)
            );
            if (r.success || r.dryRun) setShowIndexForm(false);
          }}
          dryRunMode={dryRunMode}
        />
      )}

      {/* Modal : Drop collection */}
      {showDropForm && (
        <DropCollectionModal
          collectionName={col.name}
          onClose={() => setShowDropForm(false)}
          onConfirm={async (token) => {
            const r = await onDropCollection(col.name, token);
            if (!r.success && !r.dryRun) {
              showFeedback('error', r.errors?.join(' ') || r.error);
            }
          }}
          dryRunMode={dryRunMode}
        />
      )}
    </div>
  );
}

// ─── Modal : Créer index ──────────────────────────────────────────────────

function CreateIndexModal({ collectionName, onClose, onSubmit, dryRunMode }) {
  const [fieldsRaw, setFieldsRaw] = useState('{ "field": 1 }');
  const [unique,    setUnique]    = useState(false);
  const [sparse,    setSparse]    = useState(false);
  const [ttl,       setTtl]       = useState('');
  const [parseError, setParseError] = useState(null);

  const handleSubmit = () => {
    try {
      const fields = JSON.parse(fieldsRaw);
      const options = {
        ...(unique ? { unique: true } : {}),
        ...(sparse ? { sparse: true } : {}),
        ...(ttl    ? { expireAfterSeconds: Number(ttl) } : {}),
      };
      setParseError(null);
      onSubmit(fields, options);
    } catch {
      setParseError('JSON invalide');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center">
      <div className="w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-xl overflow-hidden shadow-2xl">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
          <h4 className="text-sm font-black text-orange-300 uppercase tracking-tighter">
            Créer un Index — {collectionName}
          </h4>
          <button onClick={onClose}><X size={16} className="text-zinc-500 hover:text-white" /></button>
        </div>
        <div className="p-5 space-y-4">
          {dryRunMode && (
            <div className="px-3 py-2 bg-amber-950/30 border border-amber-800/30 rounded text-amber-300 text-xs">
              🧪 Mode DRY-RUN — l'index sera simulé, non créé
            </div>
          )}
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1.5">
              Champs (JSON)
            </label>
            <textarea
              value={fieldsRaw}
              onChange={e => setFieldsRaw(e.target.value)}
              className="w-full h-20 bg-black border border-zinc-700 rounded px-3 py-2 text-xs font-mono text-zinc-200 focus:border-orange-600/50 outline-none resize-none"
            />
            {parseError && <p className="text-red-400 text-[10px] mt-1">{parseError}</p>}
            <p className="text-zinc-700 text-[10px] mt-1">Ex: {`{ "email": 1 }`} ou {`{ "createdAt": -1 }`}</p>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={unique} onChange={e => setUnique(e.target.checked)}
                className="rounded bg-zinc-800 border-zinc-700" />
              Unique
            </label>
            <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={sparse} onChange={e => setSparse(e.target.checked)}
                className="rounded bg-zinc-800 border-zinc-700" />
              Sparse
            </label>
          </div>
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1.5">
              TTL (expireAfterSeconds) — optionnel
            </label>
            <input
              type="number"
              value={ttl}
              onChange={e => setTtl(e.target.value)}
              placeholder="Ex: 86400 (24h)"
              className="w-full bg-black border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200 focus:border-orange-600/50 outline-none"
            />
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button onClick={onClose} className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-300 text-xs">
              Annuler
            </button>
            <button onClick={handleSubmit}
              className="px-4 py-2 bg-orange-600/30 hover:bg-orange-600/50 border border-orange-500/40 rounded text-orange-200 text-xs font-medium">
              {dryRunMode ? '🧪 Simuler' : 'Créer l\'index'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Modal : Drop collection ──────────────────────────────────────────────

function DropCollectionModal({ collectionName, onClose, onConfirm, dryRunMode }) {
  const expectedToken = `DROP_${collectionName.toUpperCase()}`;
  const [token, setToken] = useState('');

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center">
      <div className="w-full max-w-md bg-zinc-900 border border-red-800/40 rounded-xl overflow-hidden shadow-2xl">
        <div className="px-5 py-4 border-b border-red-900/30 bg-red-950/20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-red-400" />
            <h4 className="text-sm font-black text-red-300 uppercase tracking-tighter">
              Supprimer la collection
            </h4>
          </div>
          <button onClick={onClose}><X size={16} className="text-zinc-500 hover:text-white" /></button>
        </div>
        <div className="p-5 space-y-4">
          {dryRunMode ? (
            <div className="px-3 py-2 bg-amber-950/30 border border-amber-800/30 rounded text-amber-300 text-xs">
              🧪 Mode DRY-RUN — la collection ne sera PAS supprimée
            </div>
          ) : (
            <div className="px-3 py-3 bg-red-950/30 border border-red-800/30 rounded text-red-300 text-xs space-y-1">
              <p className="font-bold">⚠ Action irréversible</p>
              <p>Tous les documents de <span className="font-mono">{collectionName}</span> seront définitivement supprimés.</p>
            </div>
          )}
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-widest block mb-1.5">
              Tapez <span className="font-mono text-red-400">{expectedToken}</span> pour confirmer
            </label>
            <input
              value={token}
              onChange={e => setToken(e.target.value)}
              className="w-full bg-black border border-red-800/40 rounded px-3 py-2 text-xs font-mono text-zinc-200 focus:border-red-600/50 outline-none"
              placeholder={expectedToken}
            />
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button onClick={onClose} className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-300 text-xs">
              Annuler
            </button>
            <button
              onClick={() => onConfirm(token)}
              disabled={!dryRunMode && token !== expectedToken}
              className="px-4 py-2 bg-red-700/40 hover:bg-red-700/60 border border-red-600/40 rounded text-red-200 text-xs font-medium disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {dryRunMode ? '🧪 Simuler drop' : 'Supprimer définitivement'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Vue Relations (graphe textuel) ──────────────────────────────────────

function VueRelations({ relations }) {
  if (!relations.edges?.length) {
    return (
      <div className="text-center py-12 text-zinc-700 text-sm">
        Aucune relation détectée (refs Mongoose)
      </div>
    );
  }

  // Grouper par collection source
  const bySource = relations.edges.reduce((acc, edge) => {
    if (!acc[edge.from]) acc[edge.from] = [];
    acc[edge.from].push(edge);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <p className="text-[10px] text-zinc-600 uppercase tracking-widest">
        {relations.edges.length} relation(s) détectée(s) via les refs Mongoose
      </p>
      {Object.entries(bySource).map(([source, edges]) => (
        <div key={source} className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Database size={13} className="text-orange-400" />
            <span className="text-sm font-mono font-bold text-zinc-200">{source}</span>
          </div>
          <div className="space-y-2 pl-5">
            {edges.map((edge, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="text-zinc-600 font-mono">.{edge.fromField}</span>
                <Link size={11} className="text-zinc-700" />
                <span className="text-purple-400 font-mono">{edge.to}</span>
                <Badge color={edge.type === 'one-to-many' ? 'sky' : 'purple'}>
                  {edge.type}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Panneau principal ────────────────────────────────────────────────────

export default function PanneauModeles() {
  const {
    collections, dbStats, relations,
    loading, error,
    fetchAll, createIndex, dropIndex, dropCollection,
    dryRunMode,
  } = useModelsData();

  const [search,      setSearch]      = useState('');
  const [selectedCol, setSelectedCol] = useState(null);
  const [vue,         setVue]         = useState('collections');

  const filtered = useMemo(() => {
    if (!search.trim()) return collections;
    const q = search.toLowerCase();
    return collections.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.mongooseModel?.toLowerCase().includes(q)
    );
  }, [collections, search]);

  const handleSelectCollection = (col) => {
    setSelectedCol(col);
  };

  const closeDetail = () => {
    setSelectedCol(null);
  };

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-6rem)] space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-black text-orange-300 tracking-tighter">MODÈLES DE DONNÉES</h2>
          {dbStats && (
            <p className="text-[10px] text-zinc-600 mt-0.5 uppercase tracking-widest">
              {dbStats.dbName} · {dbStats.collections} collections
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden text-xs">
            {[
              { id: 'collections', icon: Database, label: 'Collections' },
              { id: 'relations',   icon: GitBranch, label: 'Relations' },
            ].map(v => (
              <button
                key={v.id}
                onClick={() => {
                  setVue(v.id);
                  setSelectedCol(null);
                }}
                className={`flex items-center gap-1.5 px-4 py-2 transition-all ${
                  vue === v.id ? 'bg-orange-950/60 text-orange-300' : 'text-zinc-500 hover:text-zinc-300'
                }`}
              >
                <v.icon size={13} />
                {v.label}
              </button>
            ))}
          </div>

          <button onClick={fetchAll} disabled={loading} className="p-2.5 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-400 hover:text-white disabled:opacity-50">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-950/30 border border-red-800/40 rounded-xl text-red-300 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {vue === 'relations' && <VueRelations relations={relations} />}

      {vue === 'collections' && (
        <div className="flex flex-1 relative min-h-0 flex-col lg:flex-row gap-4">

          {/* Liste des collections - pleine largeur sur mobile */}
          <div className={`lg:w-80 flex flex-col gap-3 shrink-0 transition-all ${selectedCol ? 'hidden lg:flex' : 'flex'}`}>
            <div className="relative">
              <Search size={13} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher une collection..."
                className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-2xl text-sm focus:border-orange-600 outline-none"
              />
            </div> 

            <div className="flex-1 overflow-y-auto space-y-2 pr-1 no-scrollbar">
              {loading && collections.length === 0 ? (
                <div className="text-center py-12 text-zinc-600">Chargement...</div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-12 text-zinc-600">Aucune collection trouvée</div>
              ) : (
                filtered.map(col => (
                  <CollectionCard
                    key={col.name}
                    col={col}
                    isSelected={selectedCol?.name === col.name}
                    onClick={() => handleSelectCollection(col)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Détail Collection */}
          <div className={`flex-1 min-w-0 overflow-hidden border border-zinc-800/50 rounded-3xl bg-zinc-950/50 transition-all
            ${selectedCol ? 'block' : 'hidden lg:block'}`}>

            {selectedCol ? (
              <>
                {/* Bouton retour sur mobile */}
                <div className="lg:hidden p-4 border-b border-zinc-800 flex items-center">
                  <button
                    onClick={closeDetail}
                    className="flex items-center gap-2 text-orange-400 hover:text-orange-300"
                  >
                    ← Retour à la liste
                  </button>
                </div>

                <CollectionDetail
                  col={selectedCol}
                  onCreateIndex={createIndex}
                  onDropIndex={dropIndex}
                  onDropCollection={dropCollection}
                  dryRunMode={dryRunMode}
                />
              </>
            ) : (
              <div className="h-full flex items-center justify-center text-zinc-600 lg:hidden">
                <div className="text-center">
                  <Database size={48} className="mx-auto mb-4 opacity-20" />
                  <p className="text-sm">Sélectionnez une collection</p>
                </div>
              </div>
            )}
          </div>

          {/* Message sur desktop quand rien n'est sélectionné */}
          {!selectedCol && (
            <div className="hidden lg:flex flex-1 items-center justify-center text-zinc-600">
              <div className="text-center">
                <Database size={48} className="mx-auto mb-4 opacity-20" />
                <p className="text-sm">Sélectionnez une collection à gauche</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
